import { useEffect, useState } from "react"
import axios from "axios"
import { toast } from "react-toastify"
import {
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import {
  Add as AddIcon,
  Create as CreateIcon,
  Delete as DeleteIcon,
  PendingActions as PendingActionsIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  HistoryEdu as HistoryEduIcon,
} from "@mui/icons-material"
import { useKeycloak } from "../../auth/keycloakContext"
import HandleFail from "../../utils/error_handler"

// ---- Constants ----
const statusColor = {
  draft: "default",
  submitted: "info",
  district_approved: "warning",
  accepted: "success",
  rejected: "error",
}

const typeColor = {
  create: "success",
  edit: "warning",
  delete: "error",
}

const statusIconMap = {
  draft: <HistoryEduIcon />,
  submitted: <PendingActionsIcon color={statusColor.submitted} />,
  district_approved: (
    <PendingActionsIcon color={statusColor.district_approved} />
  ),
  accepted: <CheckCircleIcon color={statusColor.accepted} />,
  rejected: <CancelIcon color={statusColor.rejected} />,
}

const typeIconMap = {
  create: <AddIcon />,
  edit: <CreateIcon />,
  delete: <DeleteIcon />,
}

// ---- Helpers ----
const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleString("en-ZA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })

// ---- Components ----
const StatusIcon = ({ status }) => {
  if (!statusIconMap[status]) throw new Error("Status not recognised")
  return statusIconMap[status]
}

const TypeIcon = ({ type }) => {
  if (!typeIconMap[type]) throw new Error("Type not recognised")
  return typeIconMap[type]
}

const ConfirmButton = ({ label, color, on_confirm, disabled = false }) => {
  const [confirming, setConfirming] = useState(false)

  return (
    <>
      <Button
        color={color}
        onClick={() => setConfirming(true)}
        disabled={disabled}
      >
        {label}
      </Button>
      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to <strong>{label.toLowerCase()}</strong> this
            change?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="info" onClick={() => setConfirming(false)}>
            Abort
          </Button>
          <Button
            color={color}
            onClick={() => {
              on_confirm()
              setConfirming(false)
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

// ---- Change Request Dialog ----
const ChangeRequestDialog = ({
  change,
  set_change_id,
  set_change_requests,
  dispatch_map_data,
}) => {
  const keycloak = useKeycloak()
  const [comment, set_comment] = useState("")
  const [history, set_history] = useState([])

  useEffect(() => {
    if (!change) return
    HandleFail(async () => {
      const { data } = await axios.get(
        `/api/change_request/${change.id}/history`,
      )
      set_history(data)
    })
  }, [change])

  const {
    REACT_APP_KEYCLOAK_CLIENT_ID,
    REACT_APP_KEYCLOAK_CHANGE_REQUEST_ROLE,
    REACT_APP_KEYCLOAK_DISTRICT_APPROVAL_ROLE,
    REACT_APP_KEYCLOAK_FINAL_APPROVAL_ROLE,
  } = process.env
  const roles =
    keycloak.tokenParsed.resource_access?.[REACT_APP_KEYCLOAK_CLIENT_ID]
      ?.roles || []
  const can_final_approve = roles.includes(
    REACT_APP_KEYCLOAK_FINAL_APPROVAL_ROLE,
  )
  const can_district_approve = roles.includes(
    REACT_APP_KEYCLOAK_DISTRICT_APPROVAL_ROLE,
  )
  const can_request = roles.includes(REACT_APP_KEYCLOAK_CHANGE_REQUEST_ROLE)

  const handleClose = () => {
    set_change_id(null)
    set_history([])
    set_comment("")
  }

  const delete_change_request = async (id) => {
    HandleFail(async () => {
      await axios.delete(`/api/change_request/${id}`)
      set_change_requests((reqs) => reqs.filter((r) => r.id !== id))
      toast.success("Status updated")
      handleClose()
    })
  }

  const update_status = async (id, status, comment) => {
    HandleFail(async () => {
      if (!comment) {
        throw new Error("Please provide a comment")
      }

      const { data } = await axios.put("/api/change_request", {
        id,
        status,
        comment,
      })
      set_change_requests((reqs) => reqs.map((r) => (r.id === id ? data : r)))
      toast.success("Status updated")
      set_comment("")

      if (data.status === "accepted") {
        if (data.type === "create") {
          dispatch_map_data({
            type: "UPDATE_CUP_ID",
            payload: {
              client_id: data.data.feature.id,
              server_id: data.entity_id,
            },
          })
        } else if (data.type === "delete") {
          dispatch_map_data({
            type: "DELETE_CUP",
            payload: { id: data.entity_id },
          })
        }
      }
    })
  }

  if (!change) return null
  const { type, data, id, status } = change

  const statusActionsMap = {
    draft: [
      {
        label: "Delete",
        color: "error",
        on_confirm: () => delete_change_request(id),
      },
      {
        label: "Submit",
        color: "success",
        on_confirm: () => update_status(id, "submitted", comment),
        disabled: !can_request,
      },
    ],
    submitted: [
      {
        label: "Reject",
        color: "error",
        on_confirm: () => update_status(id, "rejected", comment),
        disabled: !can_district_approve,
      },
      {
        label: "Approve",
        color: "success",
        on_confirm: () => update_status(id, "district_approved", comment),
        disabled: !can_district_approve,
      },
    ],
    district_approved: [
      {
        label: "Reject",
        color: "error",
        on_confirm: () => update_status(id, "rejected", comment),
        disabled: !can_final_approve,
      },
      {
        label: "Approve",
        color: "success",
        on_confirm: () => update_status(id, "accepted", comment),
        disabled: !can_final_approve,
      },
    ],
  }

  return (
    <Dialog maxWidth={false} open={!!change} onClose={handleClose}>
      <DialogTitle>
        {type === "create"
          ? "Create Cup"
          : type === "edit"
            ? "Update Cup"
            : "Delete Cup"}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Name
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography>{data?.feature?.properties?.name || "-"}</Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Status
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Chip
              label={status}
              size="small"
              color={statusColor[status] || "default"}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Type
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Chip
              label={type}
              size="small"
              variant="outlined"
              color={typeColor[type] || "default"}
            />
          </Grid>
        </Grid>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Comment</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((event) => (
              <TableRow key={event.id} hover>
                <TableCell>{event.user?.username}</TableCell>
                <TableCell>
                  <Chip
                    label={event.from_status}
                    size="small"
                    color={statusColor[event.from_status] || "default"}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={event.to_status}
                    size="small"
                    color={statusColor[event.to_status] || "default"}
                  />
                </TableCell>
                <TableCell>{formatDate(event.created_at)}</TableCell>
                <TableCell>{event.comment}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {!["accepted", "rejected"].includes(status) && (
          <TextField
            label="Comment"
            fullWidth
            multiline
            sx={{ marginTop: 3 }}
            value={comment}
            onChange={(e) => set_comment(e.target.value)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button color="info" onClick={handleClose}>
          Close
        </Button>
        {statusActionsMap[status]?.map((action) => (
          <ConfirmButton key={action.label} {...action} />
        ))}
      </DialogActions>
    </Dialog>
  )
}

// ---- Main Change Requests List ----
const ChangeRequests = ({
  change_requests,
  set_change_requests,
  set_cup_geo_json,
  set_au_geo_json,
  set_cup_boundary_geo_json,
  dispatch_map_data,
}) => {
  const [change_id, set_change_id] = useState(null)

  return (
    <div>
      <Typography variant="h5" sx={{ mt: 2 }}>
        Change Requests
      </Typography>
      <List
        sx={{
          width: "100%",
          maxWidth: 360,
          bgcolor: "background.paper",
          maxHeight: "calc(100vh - 180px)",
          overflow: "auto",
        }}
      >
        {change_requests.length === 0 ? (
          <ListItem>
            <ListItemText primary="None" secondary="No changes yet" />
          </ListItem>
        ) : (
          change_requests.map((change) => (
            <ListItem
              key={change.id}
              sx={{ p: 0.5 }}
              secondaryAction={
                <IconButton edge="end" onClick={() => set_change_id(change.id)}>
                  <StatusIcon status={change.status} />
                </IconButton>
              }
            >
              <ListItemAvatar>
                <Avatar>
                  <TypeIcon type={change.type} />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={change.data.feature.properties.name}
                secondary={formatDate(change.created_at)}
              />
            </ListItem>
          ))
        )}
      </List>

      <ChangeRequestDialog
        change={change_requests.find((r) => r.id === change_id)}
        dispatch_map_data={dispatch_map_data}
        set_change_id={set_change_id}
        set_change_requests={set_change_requests}
        set_cup_geo_json={set_cup_geo_json}
        set_au_geo_json={set_au_geo_json}
        set_cup_boundary_geo_json={set_cup_boundary_geo_json}
      />
    </div>
  )
}

export default ChangeRequests
